import { memo, useCallback } from 'react'
import { Design_ } from 'src/api/db/apiDesigns'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { Column, Row } from 'src/shared/components/Semantic/all'
import {
  Accordion,
  AccordionItem,
  Card,
  Slider,
} from 'src/shared/components/ui'
import {
  AccordionTrigger,
  AccordionContent,
} from 'src/shared/components/ui/accordion'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shared/components/ui/select'
import { Label } from 'src/shared/components/ui'
import { SlidersHorizontal } from 'lucide-react'

export const FilterPicker = memo(
  ({
    design,
    setDesign,
  }: {
    design: Partial<Design_>
    setDesign: (state: Partial<Design_>) => void
  }) => {
    const { t } = useLocalization()
    const handleColorFilterChange = useCallback(
      (value: string) => {
        setDesign({
          ...design,
          colorFilter: value,
        })
      },
      [design, setDesign]
    )

    const handleOpacityChange = useCallback(
      (values: number[]) => {
        const value = values[0]
        setDesign({
          ...design,
          opacity: value,
        })
      },
      [design, setDesign]
    )

    const handleHueChange = useCallback(
      (values: number[]) => {
        const value = values[0]
        setDesign({
          ...design,
          hue: value,
        })
      },
      [design, setDesign]
    )

    const handleBrightnessChange = useCallback(
      (values: number[]) => {
        const value = values[0]
        setDesign({
          ...design,
          brightness: value,
        })
      },
      [design, setDesign]
    )

    const handleSaturationChange = useCallback(
      (values: number[]) => {
        const value = values[0]
        setDesign({
          ...design,
          saturation: value,
        })
      },
      [design, setDesign]
    )

    return (
      <Card>
        <Accordion type="single" collapsible>
          <AccordionItem value="filters" borderBottom={false}>
            <AccordionTrigger className="text-sm font-normal hover:no-underline">
              <Row className="items-center gap-2">
                <SlidersHorizontal className="w-4 h-4" />
                <p className="text-sm">{t('Filters', 'Filter')}</p>
              </Row>
            </AccordionTrigger>

            <AccordionContent className="h-[250px]">
              <Column className="gap-3 w-full mt-2">
                {/* Color Filter */}
                <Column className="gap-2">
                  <Select
                    label={t('Color filter', 'Färgfilter')}
                    value={design?.colorFilter || 'none'}
                    onValueChange={handleColorFilterChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue
                        placeholder={t('Select filter', 'Välj filter')}
                      />
                    </SelectTrigger>

                    <SelectContent>
                      <SelectItem value="none">
                        {t('Original', 'Original')}
                      </SelectItem>
                      <SelectItem value="black">
                        {t('Black', 'Svart')}
                      </SelectItem>
                      <SelectItem value="white">{t('White', 'Vit')}</SelectItem>
                    </SelectContent>
                  </Select>
                </Column>

                {/* Opacity */}
                <Column className="gap-2 ">
                  <Row className="items-center justify-between">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {t('Opacity', 'Opacitet')}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {Math.round((design?.opacity || 1) * 100)}%
                    </span>
                  </Row>

                  <Slider
                    value={[design?.opacity || 1]}
                    onValueChange={handleOpacityChange}
                    min={0}
                    max={1}
                    step={0.01}
                    className="w-full"
                  />
                </Column>

                {/* Hue */}
                <Column className="gap-2 ">
                  <Row className="items-center justify-between">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {t('Hue', 'Nyans')}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(design?.hue || 0)}°
                    </span>
                  </Row>
                  <Slider
                    value={[design?.hue || 0]}
                    onValueChange={handleHueChange}
                    min={-180}
                    max={180}
                    step={1}
                    className="w-full"
                  />
                </Column>

                {/* Brightness */}
                <Column className="gap-2 ">
                  <Row className="items-center justify-between">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {t('Brightness', 'Ljusstyrka')}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(design?.brightness || 100)}%
                    </span>
                  </Row>
                  <Slider
                    value={[design?.brightness || 100]}
                    onValueChange={handleBrightnessChange}
                    min={0}
                    max={200}
                    step={1}
                    className="w-full"
                  />
                </Column>

                {/* Saturation */}
                <Column className="gap-2 ">
                  <Row className="items-center justify-between">
                    <Label className="text-xs text-muted-foreground font-medium">
                      {t('Saturation', 'Mättnad')}
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(design?.saturation || 100)}%
                    </span>
                  </Row>

                  <Slider
                    value={[design?.saturation || 100]}
                    onValueChange={handleSaturationChange}
                    min={0}
                    max={200}
                    step={1}
                    className="w-full"
                  />
                </Column>
              </Column>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </Card>
    )
  }
)
